export const labelInsights = {
  sustainabilityPressure: {
    "No sustainability concern": {
      keywords: "disregard, no ESG policy, outdated strategy",
      intent: "Choose this if sustainability is not yet considered in strategy or operations.",
      automotive: "Legacy suppliers not audited for carbon metrics.",
      aerospace: "Tier-3 part supplier with no ESG policies.",
      electronics: "Basic assembly vendor without green certifications.",
      machinery: "Legacy factory using diesel-based operations."
    },
    "Minimal internal discussion": {
      keywords: "early awareness, boardroom talk",
      intent: "Choose if ESG is discussed internally but no roadmap exists yet.",
      automotive: "Local OEM beginning carbon awareness workshops.",
      aerospace: "Mid-size supplier acknowledging climate risks.",
      electronics: "Boardroom sustainability pitch presented.",
      machinery: "Ops team initiated green process benchmarking."
    },
    "Low visibility in roadmap": {
      keywords: "weak planning, late-stage idea",
      intent: "Select this if ESG appears in 3–5 year roadmaps but is not operationalized.",
      automotive: "Supplier roadmap includes 'net-zero' post 2030.",
      aerospace: "Long-term carbon offsets in CSR plan.",
      electronics: "Sustainability appended in innovation agenda.",
      machinery: "Listed in mission but no active KPIs."
    },
    "Customer pressure noted": {
      keywords: "buyer driven ESG, compliance alert",
      intent: "Choose this if major customers require ESG disclosures.",
      automotive: "Volkswagen mandating Scope 3 from Tier-1s.",
      aerospace: "Airbus carbon footprint disclosure requests.",
      electronics: "OEMs demand RoHS + REACH reporting.",
      machinery: "Heavy equipment clients request EPDs."
    },
    "Basic ESG commitment": {
      keywords: "formalized intent, initial commitment",
      intent: "Use this if the org has committed to ESG publicly.",
      automotive: "Tier-1 declared carbon neutral goal by 2035.",
      aerospace: "Signed SAF sourcing pledge with IATA.",
      electronics: "ESG KPIs added to annual report.",
      machinery: "Basic sustainability officer role created."
    },
    "Carbon mapping started": {
      keywords: "GHG inventory, Scope 1–2 metrics",
      intent: "Pick this if org has initiated CO2 tracking for facilities or products.",
      automotive: "Volvo started EV lifecycle footprinting.",
      aerospace: "Boeing's emission baseline phase.",
      electronics: "Apple mapping carbon by supplier region.",
      machinery: "Caterpillar quantifying plant emissions."
    },
    "Lifecycle tracking in progress": {
      keywords: "end-to-end LCA, carbon transparency",
      intent: "Select this if org maps CO2 across design → disposal.",
      automotive: "BMW LCA for i-series EVs.",
      aerospace: "LCA tool integration in CATIA.",
      electronics: "Intel PCB lifecycle carbon dashboard.",
      machinery: "Komatsu linking CAD → EOL emissions."
    },
    "Supply chain alignment": {
      keywords: "Tier-1 readiness, Scope 3",
      intent: "Choose this if supplier ESG data is integrated.",
      automotive: "Ford aligning steel/EV battery supply with ESG.",
      aerospace: "Rolls-Royce sourcing SAF-aligned vendors.",
      electronics: "ESG dashboard with contract manufacturers.",
      machinery: "John Deere tiered ESG scoring."
    },
    "Automated emissions tagging": {
      keywords: "digital thread, emission flow",
      intent: "Used if system tracks emissions automatically.",
      automotive: "PLM-linked emission tags per component.",
      aerospace: "Digital twin emission feedback loop.",
      electronics: "Automated GHG updates from supply APIs.",
      machinery: "Smart sensors feeding real-time CO2 data."
    },
    "Verified ESG reporting": {
      keywords: "audit-ready, assurance-driven",
      intent: "Select if ESG reports are 3rd-party verified.",
      automotive: "Mercedes CDP-verified reports.",
      aerospace: "Airbus external ESG audits.",
      electronics: "Dell ESG disclosures validated.",
      machinery: "Caterpillar sustainability assurance."
    },
    "Sustainability as core strategy": {
      keywords: "green-first, ESG-led growth",
      intent: "If every decision is ESG-guided, pick this.",
      automotive: "Polestar – carbon-neutral target by 2030.",
      aerospace: "Zero-emission aircraft as flagship mission.",
      electronics: "ESG KPIs integrated in R&D spend.",
      machinery: "Sustainable platform strategy."
    }
  },
  
  esgCompliance: {
    "No compliance program": {
      keywords: "non-compliant, no ESG system",
      intent: "Choose this if the organization has no formal ESG or CSRD compliance mechanism in place.",
      automotive: "Tier-3 supplier with no ESG policy or awareness of CSRD.",
      aerospace: "Small part manufacturer with no sustainability reporting.",
      electronics: "Unregistered contract manufacturer in Asia with no ESG effort.",
      machinery: "Local workshop with zero environmental compliance."
    },
    "Manual reporting only": {
      keywords: "spreadsheets, audit risk, manual data",
      intent: "Use this if ESG data is collected in Excel or paper but not digitized.",
      automotive: "Suppliers manually sending carbon data via Excel.",
      aerospace: "Local aerospace job shops using paper-based HSE logs.",
      electronics: "SME vendors using Google Sheets for emissions logs.",
      machinery: "Manual tracking of resource usage for local audits."
    },
    "Basic ESG policy in place": {
      keywords: "declaration, policy upload, PDF level",
      intent: "Choose this if there is a formal ESG/CSR policy document but not enforced across systems.",
      automotive: "Tier 2 supplier posted ESG commitment PDF on website.",
      aerospace: "Suppliers referencing ISO14001 and generic carbon goals.",
      electronics: "Vendors with downloadable RoHS/REACH policy files.",
      machinery: "OEM declared energy-saving initiatives in annual letter."
    },
    "Initial compliance checklist": {
      keywords: "self-assessment, checklist, gap analysis",
      intent: "Select this if the org uses a checklist-based ESG self-review process.",
      automotive: "Ford’s supplier compliance questionnaire initiated.",
      aerospace: "NADCAP-like checklist for ESG from Airbus supplier portal.",
      electronics: "CSR readiness checklist shared via supplier network.",
      machinery: "Self-declared compliance via spreadsheet scoring."
    },
    "Early-stage CSRD alignment": {
      keywords: "EU compliance, structured gaps",
      intent: "Choose this if org is preparing for CSRD or EU Taxonomy reporting.",
      automotive: "Volkswagen suppliers reviewing EU Green Deal readiness.",
      aerospace: "PLM updated with double materiality analysis start.",
      electronics: "PCB vendor assessing ESRS standards applicability.",
      machinery: "Light industrial firms starting CSRD data model mapping."
    },
    "PLM-linked disclosures": {
      keywords: "traceable ESG, structured data",
      intent: "Pick this if ESG disclosures are created using PLM-linked data models.",
      automotive: "OEM automates product ESG with teamcenter + T4S.",
      aerospace: "Parts tagged with emission scores in engineering BOM.",
      electronics: "Compliance dashboard pulls from design metadata.",
      machinery: "PLM integrated with eco-design disclosures."
    },
    "Audit-ready ESG reporting": {
      keywords: "assurance, defensible reports",
      intent: "Choose this if ESG reports are structured and ready for audit.",
      automotive: "Mercedes ESG framework tied to CDP & SASB indices.",
      aerospace: "Airbus ESG cross-checked with GRI & EASA frameworks.",
      electronics: "HP validated their ESG metrics for assurance.",
      machinery: "Audit trails included in emission reports in PLM."
    },
    "CSRD workflow in PLM": {
      keywords: "CSRD automation, data pipeline",
      intent: "Use if CSRD reporting workflow is integrated into PLM or product systems.",
      automotive: "Volkswagen drives CSRD workflow from BOM to report.",
      aerospace: "PLM includes CSRD workflows aligned with ESRS.",
      electronics: "BOM tools generate part-level carbon scores for CSRD.",
      machinery: "Lifecycle info tied to CSRD templates in PLM."
    },
    "Real-time ESG data exchange": {
      keywords: "API-enabled, ESG integration",
      intent: "Choose if the ESG system supports real-time inbound/outbound data exchange.",
      automotive: "Ford ESG dashboard syncs with SAP and suppliers live.",
      aerospace: "GE's supplier portal auto-feeds environmental data.",
      electronics: "Live RoHS/REACH feed into supplier PLM.",
      machinery: "Emissions data pushed to MES & PLM continuously."
    },
    "AI-supported ESG tracking": {
      keywords: "AI detection, smart inference",
      intent: "Use if AI/ML is used to estimate or predict ESG factors from operations or BOM.",
      automotive: "BMW AI maps carbon footprint per vehicle line.",
      aerospace: "Airbus uses ML to forecast energy intensity by part type.",
      electronics: "Intel uses AI to detect compliance risks from product tree.",
      machinery: "Komatsu applies ML for scope-3 inference from supply chain."
    },
    "Proactive ESG governance": {
      keywords: "strategic ESG, board-level review",
      intent: "Select this if ESG is monitored at board level and drives procurement or design.",
      automotive: "Polestar uses ESG as primary vehicle design constraint.",
      aerospace: "Safran includes ESG in CEO reviews & tender rules.",
      electronics: "Dell links ESG scores with new vendor approval.",
      machinery: "ESG dashboard used in quarterly exec reviews."
    }
  },

greenProductInnovation: {
    "No sustainable design": {
      keywords: "non-sustainable design, no eco goals",
      intent: "Choose this if products are not designed with any sustainability or recyclability considerations.",
      automotive: "Traditional ICE engine blocks without reuse or lifecycle focus.",
      aerospace: "Mechanical components made from non-recyclable alloys.",
      electronics: "Use of hazardous materials with no end-of-life recovery plan.",
      machinery: "Heavy cast parts without environmental impact evaluation."
    },
    "Basic material awareness": {
      keywords: "partial awareness, ad hoc material changes",
      intent: "Use this if greener materials are used in some areas, but not consistently or traceably.",
      automotive: "Seat foam replaced with bio-polymer but not traceable.",
      aerospace: "One-off composite swap with no digital thread.",
      electronics: "Some RoHS-compliant plastics adopted, no system integration.",
      machinery: "Greener paint materials selected in one product line."
    },
    "Recyclability considered": {
      keywords: "part-level recyclability, basic design rules",
      intent: "Pick this if parts are designed for easier disassembly and material recovery.",
      automotive: "Battery trays marked for recycling & metal recovery.",
      aerospace: "Wiring harnesses tagged for copper separation.",
      electronics: "Motherboards labeled for e-waste segregation.",
      machinery: "Steel frames designed for dismantling at end-of-life."
    },
    "Lifecycle modeling exists": {
      keywords: "early LCA awareness, eco screening",
      intent: "Select if design teams include recyclability or LCA in early phase reviews.",
      automotive: "Concept vehicle LCA triggers in BOM stage gate.",
      aerospace: "Design reviews include recyclability gate per assembly.",
      electronics: "PCB concept rules restrict lead-based solder.",
      machinery: "Digital concepts score parts for eco weight."
    },
    "Sustainable design rules applied": {
      keywords: "rule-based design, green design libraries",
      intent: "Choose this if design guidelines enforce recyclable or sustainable features.",
      automotive: "Templates enforce use of low-impact polymer grades.",
      aerospace: "Sustainable design guides available in PLM workflows.",
      electronics: "Component libraries tagged with environmental score.",
      machinery: "Material selector integrated with carbon profile."
    },
    "Eco-design validation via tools": {
      keywords: "validation software, digital LCA",
      intent: "Use if LCA tools or validation checks are run before approval.",
      automotive: "GaBi plug-in for LCA runs in CAD/PLM.",
      aerospace: "Eco-audits on assemblies within engineering process.",
      electronics: "LCA software checks Bill of Materials.",
      machinery: "Validation tools show CO2 score of designs."
    },
    "End-of-life simulations used": {
      keywords: "carbon KPIs, design incentives",
      intent: "Select if design teams have carbon or environmental KPIs.",
      automotive: "Design KPIs include vehicle CO2/gm targets.",
      aerospace: "Engineering goals linked to fuel burn and weight.",
      electronics: "Low energy components mandated in designs.",
      machinery: "KPIs tied to energy consumption per output unit."
    },
    "Circular design features embedded": {
      keywords: "modular, reusable design",
      intent: "Pick if design favors reuse, repair, or modular rebuild.",
      automotive: "Battery swap modules integrated in EV design.",
      aerospace: "Reconfigurable interiors for reuse across platforms.",
      electronics: "Replaceable screen modules to reduce e-waste.",
      machinery: "Parts engineered for overhaul not disposal."
    },
    "Digital twin includes sustainability metrics": {
      keywords: "LCA-integrated twin, traceable emissions",
      intent: "Choose if digital twin models include carbon or recyclability values.",
      automotive: "Vehicle twin includes GHG data by part.",
      aerospace: "Twin models simulate CO2 across aircraft ops.",
      electronics: "Twin simulates lifecycle e-waste footprint.",
      machinery: "CO2 and energy usage simulated in real-world twin."
    },
    "Customer-visible green scoring": {
      keywords: "green labels, public facing",
      intent: "Use this if customers can view sustainability score per product.",
      automotive: "EcoScore visible on vehicle configurators.",
      aerospace: "Eco-rating shown in aircraft tenders.",
      electronics: "Sustainability label on consumer devices.",
      machinery: "Green badge shown for Tier-4 engines."
    },
    "Green-first product innovation culture": {
      keywords: "sustainability-led innovation, eco design DNA",
      intent: "Select if green performance is treated as core design principle across org.",
      automotive: "Polestar targets zero-carbon car by 2030.",
      aerospace: "Hydrogen aircraft programs integrated in roadmap.",
      electronics: "Fairphone uses circular economy as strategy.",
      machinery: "John Deere invests in sustainable tractors and green platforms."
    }
  },



supplyChainCircularity: {
    "No circularity practices": {
      keywords: "linear supply chain, no reuse strategy",
      intent: "Select this if your supply chain has no policies or actions for reuse, recycling, or circular economy.",
      automotive: "Tier 2 suppliers send parts in disposable packaging with no return mechanism.",
      aerospace: "Machining waste discarded without tracking material lifecycle.",
      electronics: "Vendors supply plastic-wrapped boards with no take-back.",
      machinery: "One-time-use metal crates for export components."
    },
    "Basic return logistics": {
      keywords: "core returns, vendor backhaul",
      intent: "Choose this if used items are collected back via manual or ad hoc logistics.",
      automotive: "Battery cores returned manually to remanufacturing facilities.",
      aerospace: "Landing gear sent back for refurb via MRO vendors.",
      electronics: "Customers return smartphones to store dropboxes.",
      machinery: "Engine cores sent back to reman partners occasionally."
    },
    "Recyclable packaging programs": {
      keywords: "eco-packaging, reusables",
      intent: "Pick this if supply chain uses recyclable or reusable packaging consistently.",
      automotive: "Returnable packaging crates shared with suppliers.",
      aerospace: "Reusable bulk containers for avionics parts.",
      electronics: "Foamless recyclable boxes for laptops.",
      machinery: "Plastic pallets and reusable bins across plants."
    },
    "Component take-back initiated": {
      keywords: "reclaim programs, reverse logistics",
      intent: "Use this if component or product take-back programs have started officially.",
      automotive: "OEMs take back EV motors for rare earth recovery.",
      aerospace: "End-of-life electronics recycled through approved vendors.",
      electronics: "Take-back of chargers and batteries in pilot cities.",
      machinery: "OEM initiated buyback for used engines."
    },
    "Disassembly-friendly design": {
      keywords: "modular, screw-based assemblies",
      intent: "Choose this if designs include features to simplify disassembly or reuse.",
      automotive: "Battery modules designed with non-permanent fasteners.",
      aerospace: "Avionic trays redesigned for easier extraction.",
      electronics: "Products avoid adhesives to allow part reuse.",
      machinery: "Hydraulic modules designed with tool-free access."
    },
    "Material recovery targets": {
      keywords: "measurable recovery, reclaim quotas",
      intent: "Pick this if org has set % recovery goals from supply chain waste.",
      automotive: "Steel and aluminum recovery target of 85% per car.",
      aerospace: "Titanium offcut recovery goal of 65%.",
      electronics: "Recovery goals for copper and precious metals.",
      machinery: "Scrap recovery percentages tracked in PLM."
    },
    "Supplier recyclability requirements": {
      keywords: "vendor circularity policy",
      intent: "Use this if suppliers are required to meet circular economy targets.",
      automotive: "OEM mandates packaging return process from all suppliers.",
      aerospace: "Vendor scorecard includes recyclability score.",
      electronics: "PCBA vendors submit take-back readiness checklists.",
      machinery: "Vendors graded on compliance with circularity checklist."
    },
    "Lifecycle traceability in supply chain": {
      keywords: "digital twin, cradle-to-cradle",
      intent: "Choose this if lifecycle info is traceable from suppliers across product life.",
      automotive: "Battery lifecycle tracking across suppliers using PLM.",
      aerospace: "Traceability from material mill to aircraft retirement.",
      electronics: "Raw materials digitally tagged through BOM lifecycle.",
      machinery: "Supplier parts tracked cradle-to-grave digitally."
    },
    "Circularity dashboards for partners": {
      keywords: "supplier ESG cockpit",
      intent: "Select if partners can view/report circularity metrics via dashboards.",
      automotive: "ESG metrics per shipment shown to suppliers.",
      aerospace: "Circularity KPIs shared on supplier portal.",
      electronics: "Green compliance reports by vendor tier.",
      machinery: "Supplier dashboard includes packaging returns & reuse rate."
    },
    "Product-as-a-Service integration": {
      keywords: "servitization, shared ownership",
      intent: "Pick this if your circular supply chain supports products offered as-a-service.",
      automotive: "OEM offers fleet vehicles on subscription with asset reuse.",
      aerospace: "Engine OEM tracks reuse cycles in PaaS models.",
      electronics: "Smartphone-as-a-service with 100% take-back guarantee.",
      machinery: "Industrial tool subscription model with circular asset recovery."
    },
    "Closed-loop supply network": {
      keywords: "full circularity, zero-waste chain",
      intent: "Select if most components/materials are reclaimed, reused, and cycled back across supply chain.",
      automotive: "Volvo Cars targets 100% circular material flows by 2040.",
      aerospace: "Safran’s closed-loop reclaim of turbine blades.",
      electronics: "Fairphone achieves 85% material recovery in closed loop.",
      machinery: "John Deere reman program loops engine and driveline parts."
    }
  },
supplierDependency: {
    "Fully in-house production": {
      keywords: "no supplier reliance, internal manufacturing",
      intent: "Choose if almost all manufacturing, design, or engineering is done in-house with minimal external supplier dependency.",
      automotive: "OEM with in-house drivetrain, electronics, and stamping lines.",
      aerospace: "Fuselage and avionics manufactured internally by OEM.",
      electronics: "Chip design and manufacturing handled by same organization.",
      machinery: "Custom manufacturing and part machining under one roof."
    },
    "Minimal dependency for non-core items": {
      keywords: "outsourced commodities, core retained",
      intent: "Select if only standard or commodity parts are procured externally while all core design/IP is managed in-house.",
      automotive: "Only bolts and consumables are outsourced; core systems designed in-house.",
      aerospace: "Consumables and brackets purchased, while flight control software is internal.",
      electronics: "External sourcing of screws and cables; internal SoC design.",
      machinery: "Fasteners and packaging outsourced; machine heads developed internally."
    },
    "Strategic supplier usage for capacity": {
      keywords: "scalable sourcing, trusted partners",
      intent: "Pick if suppliers are engaged to scale capacity, not due to capability gaps.",
      automotive: "Extra ECU manufacturing outsourced during volume surge.",
      aerospace: "Tier-1 vendor supports wing fabrication due to schedule compression.",
      electronics: "Second supplier added for touchscreens during peak demand.",
      machinery: "Casting support used for overflow during seasonal orders."
    },
    "Dependence on niche suppliers": {
      keywords: "specialized sourcing, narrow vendor pool",
      intent: "Choose this if niche or custom components are available from limited vendors.",
      automotive: "Single vendor for rare earth magnets used in motors.",
      aerospace: "Single-supplier epoxy resin for composite wings.",
      electronics: "Niche thermal management component with only 2 global suppliers.",
      machinery: "Precision tool vendor locked due to proprietary mount interface."
    },
    "Single-source for critical systems": {
      keywords: "vendor lock-in, risk concentration",
      intent: "Use if one vendor supplies entire system crucial to product.",
      automotive: "One supplier provides all infotainment modules across models.",
      aerospace: "Avionics suite from a single Tier-1 vendor across all platforms.",
      electronics: "Entire charging block stack sourced from one ODM.",
      machinery: "Hydraulic system vendor controls entire pump + valves unit."
    },
    "Multiple suppliers per domain": {
      keywords: "redundant sourcing, supplier alternatives",
      intent: "Pick if multiple vendors exist per domain to mitigate dependency.",
      automotive: "Battery pack components from 2–3 interchangeable vendors.",
      aerospace: "Flight deck systems have approved dual suppliers.",
      electronics: "Panel glass from Corning and regional backup vendors.",
      machinery: "Casting vendor network set for 2+ regional fallbacks."
    },
    "Global vendor pool with strategic mix": {
      keywords: "geographic balance, dual-sourcing",
      intent: "Select if supplier network is spread across geographies for agility and risk control.",
      automotive: "Asia + Europe + NAFTA vendor mix for key subsystems.",
      aerospace: "North America and EU suppliers for turbine modules.",
      electronics: "Supply redundancy between Taiwan and Vietnam.",
      machinery: "Indian + EU vendors co-source casting and bearings."
    },
    "Suppliers involved in product co-design": {
      keywords: "collaborative design, DFM with vendors",
      intent: "Choose if suppliers are actively engaged in product co-development.",
      automotive: "Tier-1s co-design battery thermal systems.",
      aerospace: "Interior vendors contribute to modular platform innovation.",
      electronics: "ODM collaborates on PCB routing rules.",
      machinery: "Suppliers help optimize machine casting geometry in CAD stage."
    },
    "JIT or sequenced supplier delivery": {
      keywords: "just-in-time, lean supply chain",
      intent: "Use this if supplier delivery is tightly integrated with production sequencing.",
      automotive: "Seats delivered within 90-minute windows to line-side.",
      aerospace: "Wing subassemblies sequenced to final assembly dock.",
      electronics: "Motherboards shipped directly to box build assembly.",
      machinery: "Sub-frame assemblies delivered same day for final fit."
    },
    "Digital integration with suppliers": {
      keywords: "API-enabled collaboration, system sync",
      intent: "Select if PLM, ERP or MES is integrated with supplier systems for data flow.",
      automotive: "T4S integration allows direct supplier BOM updates.",
      aerospace: "PLM gateway enables spec change visibility in real-time.",
      electronics: "Supply portal pushes alerts to ODMs on design changes.",
      machinery: "Drawings sent directly to supplier via PLM + secure viewer."
    },
    "Highly distributed and autonomous supplier network": {
      keywords: "ecosystem orchestration, vendor-driven innovation",
      intent: "Pick this if suppliers are not just executors but strategic innovation nodes in network.",
      automotive: "Battery cell partners co-own IP and factories.",
      aerospace: "Tier-1s create entire integrated modules with roadmap alignment.",
      electronics: "ODM designs, tests, and owns product lifecycle remotely.",
      machinery: "Reman vendors manage full lifecycle and feedback loop into design."
    }
  },
  collaborationNeed: {
    "Siloed departments, no integration": {
      keywords: "disconnected teams, no data sharing",
      intent: "Choose if departments work independently with no structured collaboration or shared systems.",
      automotive: "Design, manufacturing, and service use separate tools with no data handoff.",
      aerospace: "Stress analysis and design teams work in separate silos.",
      electronics: "Board designers and firmware teams don’t interact until final stage.",
      machinery: "Mechanical and controls teams use incompatible formats."
    },
    "Email-based collaboration": {
      keywords: "manual exchange, no traceability",
      intent: "Select this if collaboration primarily occurs through email threads or offline methods.",
      automotive: "Specs and drawings shared via email attachments.",
      aerospace: "Design changes approved via email and stored manually.",
      electronics: "Excel sheets shared over email for BOM updates.",
      machinery: "Feedback from manufacturing sent via PDFs in email."
    },
    "Periodic sync meetings": {
      keywords: "status updates, meeting-driven",
      intent: "Use this if collaboration is limited to scheduled syncs and manual tracking.",
      automotive: "Cross-functional team syncs weekly to track design issues.",
      aerospace: "Design-to-production reviews occur at milestone gates.",
      electronics: "Monthly alignment between layout and mechanical teams.",
      machinery: "Process engineers meet monthly with design office."
    },
    "Basic shared platforms in place": {
      keywords: "file-based sharing, intranet",
      intent: "Choose this if shared folders, cloud drives, or basic PLM is used for collaboration.",
      automotive: "Teams access shared BOMs via internal SharePoint.",
      aerospace: "Revision-controlled files in company intranet.",
      electronics: "Google Drive used for part spec collaboration.",
      machinery: "Drawings stored in shared folders accessed by multiple teams."
    },
    "Role-based access to shared data": {
      keywords: "access control, secure views",
      intent: "Pick if teams access a common system with permissioned access.",
      automotive: "Only sourcing can edit cost info in shared PLM.",
      aerospace: "Manufacturing has view-only access to design intent.",
      electronics: "R&D can access mechanical libraries in PLM.",
      machinery: "Maintenance can view 3D models from PLM viewer."
    },
    "Task-level assignment and notification": {
      keywords: "workflow triggers, assigned actions",
      intent: "Select this if collaboration involves task assignments and notifications via tools.",
      automotive: "ECRs assigned to domain leads with due dates.",
      aerospace: "Design validation routed automatically to stakeholders.",
      electronics: "Part reviews triggered by revision workflow.",
      machinery: "Line balancing tasks assigned to process engineers."
    },
    "Cross-domain data linking": {
      keywords: "design-process link, traceable handover",
      intent: "Choose if data links exist between mechanical, electrical, and software domains.",
      automotive: "ECAD-PCB linked to mechanical harness layout.",
      aerospace: "System model linked to 3D structure for simulation.",
      electronics: "Firmware version tied to specific hardware revision.",
      machinery: "CAD and PLC logic connected in design portal."
    },
    "Supplier collaboration via secure portal": {
      keywords: "external collaboration, gated access",
      intent: "Pick if suppliers access product data via a secure portal with roles.",
      automotive: "Suppliers receive updates and specs via Teamcenter supplier portal.",
      aerospace: "Design reviews held over supplier-accessible PLM interface.",
      electronics: "ODM accesses 3D and spec sheets for configuration alignment.",
      machinery: "Vendors get PDF, STEP and requirement snapshots from portal."
    },
    "Real-time co-editing or feedback": {
      keywords: "live comments, concurrent collaboration",
      intent: "Use if teams can comment, review, or edit data together in real-time.",
      automotive: "Multiple engineers co-edit BOP plans in Active Workspace.",
      aerospace: "Digital mockup review allows live annotations.",
      electronics: "Live co-authoring of system design using model-based tools.",
      machinery: "Virtual design reviews using cloud PLM sessions."
    },
    "Workflow orchestration across teams": {
      keywords: "multi-role coordination, automated transitions",
      intent: "Select this if workflows span teams with conditional routing and tracking.",
      automotive: "Change process auto-routed from engineering to sourcing to validation.",
      aerospace: "System safety reviews trigger downstream tasks in DO-178 process.",
      electronics: "Approved configuration change auto-triggers testing team tasks.",
      machinery: "Digital BOP change updates line balancing team automatically."
    },
    "AI-assisted or intelligent collaboration": {
      keywords: "smart agent, ML recommendations",
      intent: "Choose if AI/ML agents assist in collaboration by surfacing risks, routing work, or suggesting reviewers.",
      automotive: "AI suggests reviewers for design change based on history.",
      aerospace: "ML bot flags dependency conflict in simulation loop.",
      electronics: "Recommendation engine alerts based on missed workflow.",
      machinery: "AI flags missing safety review step from configuration change."
    }
  },
geographicSpread: {
    "Single-site operations": {
      keywords: "centralized, local-only",
      intent: "Select this if product development, manufacturing, and services all occur in one location.",
      automotive: "Local auto parts supplier with design and production in one city.",
      aerospace: "Tool manufacturer operating from a single facility.",
      electronics: "Design and final assembly all done in one regional plant.",
      machinery: "Family-owned workshop managing everything under one roof."
    },
    "Single-country operations": {
      keywords: "national scale, no offshore",
      intent: "Choose this if all operations are within one country but span multiple facilities.",
      automotive: "Tier-2 supplier with 3 locations across India for design and manufacturing.",
      aerospace: "Component maker with plants across the U.S.",
      electronics: "R&D in Bangalore and production in Pune.",
      machinery: "Head office and factory distributed across states."
    },
    "Design-only in HQ region": {
      keywords: "localized R&D, offshore execution",
      intent: "Use this if all product design is done in HQ, while manufacturing is distributed.",
      automotive: "Detroit-based design with overseas component manufacturing.",
      aerospace: "Aircraft interiors designed in France, assembled globally.",
      electronics: "Product designed in California, produced in Taiwan.",
      machinery: "Mechanical design in Germany, parts sourced from Eastern Europe."
    },
    "Dual-region engineering and build": {
      keywords: "shared footprint, regional centers",
      intent: "Pick if both engineering and production exist in two major regions.",
      automotive: "EU and NA design and production centers for electric vehicles.",
      aerospace: "Structures designed in the UK and built in the U.S.",
      electronics: "R&D in Korea, manufacturing in Vietnam and Mexico.",
      machinery: "Design in Italy, manufacturing in China and Brazil."
    },
    "Global manufacturing footprint": {
      keywords: "multi-site build, decentralized",
      intent: "Select if production spans 3 or more continents.",
      automotive: "Vehicles assembled in EU, US, China, and India.",
      aerospace: "Aircraft components built in NA, EU, APAC regions.",
      electronics: "Mass production in China, backups in Eastern Europe & LatAm.",
      machinery: "Production lines in EU, CN, MX with interchangeable supply."
    },
    "Multi-region R&D hubs": {
      keywords: "distributed design, global IP",
      intent: "Choose this if design is done from multiple locations globally.",
      automotive: "ADAS and EV drive units designed in Sweden and California.",
      aerospace: "Simulations done in India, integration in Canada.",
      electronics: "Industrial design in Japan, SoC in the US.",
      machinery: "Controls designed in Germany, structure in China."
    },
    "Design anywhere, build anywhere capability": {
      keywords: "global sync, flexible execution",
      intent: "Use if the org is capable of fully remote and distributed design-to-build transition.",
      automotive: "Digital mockups accessible across global teams for EV platform.",
      aerospace: "Platform twin allows any partner to contribute to build.",
      electronics: "Design files sync via cloud across 5 geographies.",
      machinery: "Design data reused across plants with zero rework."
    },
    "Supplier footprint across continents": {
      keywords: "global vendor map, coverage",
      intent: "Pick if suppliers exist across regions for risk mitigation or proximity.",
      automotive: "Battery cells sourced from Asia and North America.",
      aerospace: "Tier-1s in Japan, US, and Germany support programs.",
      electronics: "ODM networks in Taiwan, Thailand, Mexico.",
      machinery: "Frame suppliers in EU, India, and Brazil."
    },
    "Customer base spans global regions": {
      keywords: "worldwide delivery, regional support",
      intent: "Choose this if end products are delivered and supported globally.",
      automotive: "OEM exports cars to over 50 countries.",
      aerospace: "Aircraft interiors shipped globally, installed by partners.",
      electronics: "Consumer devices shipped worldwide via e-com and retail.",
      machinery: "Machines sold and serviced in 80+ countries."
    },
    "Real-time coordination across geographies": {
      keywords: "live sync, time-zone optimized collaboration",
      intent: "Use if org has systems that enable real-time updates across time zones.",
      automotive: "Design updates propagated live across regional teams.",
      aerospace: "Change notices flow through PLM to all regions instantly.",
      electronics: "Cloud-first workflow syncs dev centers.",
      machinery: "Live dashboard showing build stage across factories."
    },
    "Resilience to regional disruption": {
      keywords: "geo-risk tolerance, fallback systems",
      intent: "Select this if geographic strategy includes disruption-resilient plans.",
      automotive: "Backup vendors activated during China lockdowns.",
      aerospace: "Brexit fallback included Ireland and NA expansion.",
      electronics: "Components rerouted during Taiwan earthquake.",
      machinery: "EU-China dual-sourcing ensured COVID continuity."
    }
  },
supplierToolsGap: {
    "Paper-based communication": {
      keywords: "manual, offline, no digital tools",
      intent: "Select this if most suppliers use paper drawings, PDFs, and manual approvals with no digital toolchain.",
      automotive: "Tier-3 suppliers sending hand-marked change requests via fax/email.",
      aerospace: "Machine shop uses printed drawings and handwritten measurement logs.",
      electronics: "Suppliers ask for Gerber files via USB or physical media.",
      machinery: "Component vendor requests changes by physical document courier."
    },
    "Email + spreadsheet driven processes": {
      keywords: "semi-digital, file-based",
      intent: "Choose if suppliers use spreadsheets and email but no PLM, CAD, or digital workflow support.",
      automotive: "Suppliers manage BOM updates with Excel files and mail threads.",
      aerospace: "Change approval tracked via email chains and attached Word docs.",
      electronics: "Passive component vendors use Excel forms to confirm specs.",
      machinery: "Quoting and tolerancing handled through Excel + PDFs."
    },
    "Minimal CAD access (view-only)": {
      keywords: "limited visualization, no CAD authoring",
      intent: "Use if suppliers rely on basic CAD viewers or PDF markups only.",
      automotive: "Supplier uses JT2Go to view 3D models, but cannot modify.",
      aerospace: "PDF drawings provided, markup done in Adobe tools.",
      electronics: "View-only online portal access for PCB layout.",
      machinery: "3D viewer access, but no feature editing or tolerancing tools."
    },
    "Some vendors CAD/CAM capable": {
      keywords: "basic digital maturity, isolated tooling",
      intent: "Pick if a few suppliers use CAD/CAM for internal processing, not for collaborative purposes.",
      automotive: "Stamping vendor uses 2D CAD to interpret designs.",
      aerospace: "CAM tools used by tool shop, but not integrated with OEM.",
      electronics: "SMD vendors do in-house CAM but need static inputs.",
      machinery: "Casting vendor creates molds in digital but sends back images only."
    },
    "Tools for design review but no integration": {
      keywords: "manual collaboration, isolated tools",
      intent: "Select if digital tools are used but not connected with OEM systems.",
      automotive: "Vendor reviews CAD in Creo but sends feedback via email.",
      aerospace: "Suppliers use CATIA but aren't PLM integrated.",
      electronics: "CAD screenshots marked and emailed back.",
      machinery: "Tooling vendor has internal PDM but no OEM connectivity."
    },
    "File-based digital exchange workflows": {
      keywords: "secure file exchange, gateway-based",
      intent: "Use this if files are exchanged using secure portals, but no API or data links.",
      automotive: "OEM sends STEP/IGES files via supplier gateway.",
      aerospace: "PLM creates zipped packages for suppliers.",
      electronics: "Spec files uploaded and downloaded from portal.",
      machinery: "DWG and BOM files shared through access-controlled portal."
    },
    "Live collaboration via portal": {
      keywords: "PLM portal, online markup",
      intent: "Pick this if vendors can interact with models or data live in web portal.",
      automotive: "Supplier marks JT file directly in OEM’s supplier portal.",
      aerospace: "Interactive dashboard to respond to change requests.",
      electronics: "ODM reviews system BOM in cloud workspace.",
      machinery: "Weld fixture vendor collaborates via web PLM module."
    },
    "Partial process integration (PLM or ERP)": {
      keywords: "workflow sync, PLM-triggered",
      intent: "Use this if supplier systems are integrated for at least one process—e.g., part request, RFQ.",
      automotive: "Change requests sync to SAP portal at vendor.",
      aerospace: "Supplier receives tasks via Teamcenter gateway.",
      electronics: "Sourcing systems connected to RFQ response tracker.",
      machinery: "ERP triggers supplier capacity updates to internal PLM."
    },
    "Full BOM & Drawing Exchange Integration": {
      keywords: "end-to-end visibility, BOM handoff",
      intent: "Choose this if suppliers receive up-to-date designs and can submit revisions or comments via system.",
      automotive: "Vendors submit proposed design changes via PLM.",
      aerospace: "Auto-sync of drawings + BOM between OEM and Tier-1.",
      electronics: "Approved PCBA vendors receive controlled BoMs via workflow.",
      machinery: "Drawings flow with revision tracking to vendor systems."
    },
    "Supplier-side simulation and validation tools": {
      keywords: "DFM/FEA capability, advanced tools",
      intent: "Pick this if suppliers use advanced digital tools like FEA, CFD, or DFM validation on models.",
      automotive: "Supplier runs crash simulations on frame subassemblies.",
      aerospace: "Winglet supplier performs stress analysis on own CAD.",
      electronics: "ODM uses signal integrity tools for layout validation.",
      machinery: "Fabrication vendor uses weld deformation simulation tools."
    },
    "Digital twin compatibility across supplier network": {
      keywords: "twin-ready, fully digital supply mesh",
      intent: "Select if vendors can consume, update, or build part of a digital twin collaboratively.",
      automotive: "Battery pack supplier integrates digital twin data from cell to vehicle.",
      aerospace: "Engine casing vendor contributes digital performance data.",
      electronics: "Digital product twins extended to EMS partners.",
      machinery: "Vendors update build state and condition in real-time to twin."
    }
  },
supplierSLAPressure: {
    "No formal SLAs with suppliers": {
      keywords: "informal, no contract pressure",
      intent: "Select this if supplier performance is not governed by defined SLAs or KPIs.",
      automotive: "Tool vendor not bound by delivery timeline commitments.",
      aerospace: "Vendor selected based on trust, not on documented criteria.",
      electronics: "No contractual obligations on delivery, quality, or cost.",
      machinery: "Suppliers used based on long-term relationships only."
    },
    "Basic delivery time expectations": {
      keywords: "timing only, verbal agreements",
      intent: "Choose if suppliers are expected to deliver on time but with no formal penalties.",
      automotive: "Suppliers asked to deliver in 2 weeks, without SLA.",
      aerospace: "Expectation of delivery before build start but no escalation.",
      electronics: "Rough schedule given but no formal document.",
      machinery: "Target date agreed but not tracked strictly."
    },
    "First-level SLA contracts for cost & quality": {
      keywords: "basic SLA, fixed cost & quality clauses",
      intent: "Pick this if suppliers sign agreements defining acceptable defect and cost targets.",
      automotive: "Suppliers required to maintain <1% defects.",
      aerospace: "Formal contracts signed with delivery and quality terms.",
      electronics: "Cost variation limited to 2% as per agreement.",
      machinery: "Weld quality target added to purchase agreement."
    },
    "Delivery delay penalties defined": {
      keywords: "on-time metrics, late fees",
      intent: "Use this if late delivery directly leads to contractual penalties or clawbacks.",
      automotive: "Every day late incurs $2K fee.",
      aerospace: "Delayed avionics module incurs per-hour penalty.",
      electronics: "Missed shipping deadline triggers procurement escalation.",
      machinery: "Late delivery triggers 1% reduction in payment."
    },
    "Rework or defect penalties enforced": {
      keywords: "return cost, field defects",
      intent: "Choose this if suppliers are penalized for rework costs or warranty issues.",
      automotive: "Supplier bears rework cost of bad batch.",
      aerospace: "Warranty failure traced back to supplier incurs chargeback.",
      electronics: "RMA and shipping costs billed to ODM.",
      machinery: "Failed assembly test rerun cost deducted from vendor invoice."
    },
    "Tiered SLA with escalation matrix": {
      keywords: "priority levels, defined response times",
      intent: "Select if SLA defines response expectations for issues based on severity.",
      automotive: "Critical issues require 4-hour response from supplier.",
      aerospace: "Escalation to vendor VP if delivery slips >48h.",
      electronics: "ODM response within 1 hour on blocker-level alert.",
      machinery: "Customer line stoppage → vendor hotline triggered."
    },
    "Real-time SLA tracking system in place": {
      keywords: "live SLA dashboard, auto alerts",
      intent: "Pick this if SLAs are tracked automatically with dashboard metrics visible to stakeholders.",
      automotive: "Supplier portal shows OTD, defect % in real-time.",
      aerospace: "KPI trends available for each vendor.",
      electronics: "Live monitoring of all POs for SLA violations.",
      machinery: "Vendor dashboard with performance heatmaps."
    },
    "Incentive-based SLA with rewards": {
      keywords: "positive reinforcement, bonus",
      intent: "Use this if suppliers receive bonuses or recognition for exceeding SLAs.",
      automotive: "Award issued for 99.9% on-time delivery.",
      aerospace: "10% bonus for >98% defect-free runs.",
      electronics: "Preferred vendor status for early delivery.",
      machinery: "Annual rebate given based on aggregate performance."
    },
    "Dynamic SLA renegotiation mechanism": {
      keywords: "adaptive SLA, context-aware",
      intent: "Choose this if SLA terms adjust based on production, quality, or strategic shifts.",
      automotive: "Battery vendors renegotiate SLA during model ramp-up.",
      aerospace: "Quality threshold adjusted for new materials trial.",
      electronics: "ODM allows dynamic priority tuning based on product roadmap.",
      machinery: "Lead time targets eased during part shortages."
    },
    "AI-based prediction of SLA risks": {
      keywords: "ML risk scoring, predictive SLA",
      intent: "Pick this if system flags probable SLA breaches ahead of time using analytics.",
      automotive: "Delay risk flagged based on historical PO behavior.",
      aerospace: "Vendor risk heatmap predicts upcoming failure trends.",
      electronics: "ML model tags incoming shipments as high delay-risk.",
      machinery: "AI flags supplier capacity risk based on external factors."
    },
    "Blockchain-based SLA enforcement and logging": {
      keywords: "immutable SLA ledger, trustless contracts",
      intent: "Select if SLA agreements, actions, and breaches are recorded on distributed ledger for trust and audit.",
      automotive: "Smart contract auto-enforces battery module penalties.",
      aerospace: "Supplier SLA breach logged immutably for audit.",
      electronics: "Each shipment SLA tokenized with time, defect, sign-off.",
      machinery: "Delay penalties and response timestamps recorded in DLT log."
    }
  },
partnerEcosystemMaturity: {
    "Mostly manual, non-digital partners": {
      keywords: "low digital capability, manual workflows",
      intent: "Select if most partners lack digital infrastructure or use paper/email processes.",
      automotive: "Small vendors without CAD or PLM access.",
      aerospace: "Tooling suppliers rely on printed drawings.",
      electronics: "ODM responds using PDFs, no digital collaboration tools.",
      machinery: "Local suppliers use manual BOM tracking."
    },
    "Some partners use CAD or ERP": {
      keywords: "basic software use, disconnected systems",
      intent: "Choose if a few partners use CAD or ERP systems but lack integration or collaboration.",
      automotive: "Vendor uses SolidWorks, shares designs via email.",
      aerospace: "Component makers have ERP but no visibility into OEM schedule.",
      electronics: "PCB vendor runs basic ERP but no DFM tools.",
      machinery: "Fabrication vendor uses local ERP to manage capacity."
    },
    "Partners familiar with PLM deliverables": {
      keywords: "file-based collaboration, PLM ready",
      intent: "Select if partners are capable of consuming and delivering PLM-compliant outputs.",
      automotive: "Vendors deliver JT files and revision-controlled drawings.",
      aerospace: "Suppliers submit parts with PLM meta-data fields populated.",
      electronics: "Parts delivered with structured XML BoM.",
      machinery: "Weld assemblies shared in native and lightweight formats."
    },
    "Regular data exchange with partners": {
      keywords: "periodic sync, digital interface",
      intent: "Pick this if partners submit or receive data using APIs, portals, or file-drop systems.",
      automotive: "Secure file transfers with part number and revision history.",
      aerospace: "T4S gateway syncs specs and tolerances periodically.",
      electronics: "Spec changes and RFQs managed in vendor portal.",
      machinery: "BOMs exchanged via XML gateway weekly."
    },
    "Collaborative issue management with partners": {
      keywords: "shared issue tracking, co-resolution",
      intent: "Choose if digital systems support tracking and resolving issues collaboratively.",
      automotive: "Defect issues opened and resolved in shared dashboard.",
      aerospace: "Configuration mismatches logged with root cause traceability.",
      electronics: "ODM flags manufacturability issues with annotations.",
      machinery: "Supplier creates weld tolerance feedback linked to CAD."
    },
    "Partners participate in design reviews": {
      keywords: "DFM inputs, early feedback loop",
      intent: "Use this if partners are involved in early design reviews or validations.",
      automotive: "Battery supplier participates in packaging mockups.",
      aerospace: "Interior vendor reviews cabin design before release.",
      electronics: "ODM comments on layout heat zones in initial designs.",
      machinery: "Vendor suggests frame changes in design kickoff."
    },
    "Integrated design and manufacturing processes": {
      keywords: "connected systems, value-chain integration",
      intent: "Pick this if design-to-manufacturing flow is seamless across partners.",
      automotive: "Design output directly usable in vendor CAM systems.",
      aerospace: "Supplier builds from digital design BOMs directly.",
      electronics: "PLM release triggers procurement and assembly at ODM.",
      machinery: "Weld jigs and CNC fixtures derived from shared design environment."
    },
    "Automated SLA and quality performance reporting": {
      keywords: "vendor dashboards, auto-tracking",
      intent: "Select if KPIs and SLA compliance are tracked and shared automatically.",
      automotive: "Portal shows each supplier's delivery and defect metrics.",
      aerospace: "Live scorecard ranks partners by schedule and cost adherence.",
      electronics: "OTD and quality metrics updated daily.",
      machinery: "Supplier performance audit available digitally."
    },
    "Multi-tier partner visibility and traceability": {
      keywords: "T1–Tn awareness, upstream linkage",
      intent: "Use this if system allows visibility across multiple tiers of supply and integration.",
      automotive: "Tier 1 and 2 tracked together via digital thread.",
      aerospace: "Upstream traceability from fasteners to fuselage.",
      electronics: "ODM and component vendors mapped for trace.",
      machinery: "Cast parts tracked across sub-vendor network."
    },
    "Partners participate in digital thread or twin": {
      keywords: "ecosystem sync, model-based",
      intent: "Pick this if partners contribute to, or consume from, your digital thread or twin.",
      automotive: "Thermal models from supplier linked to full vehicle twin.",
      aerospace: "Stress analysis results included in aircraft digital twin.",
      electronics: "Schematic model + sensor data fed by EMS partner.",
      machinery: "Lifecycle data from vendor sensors added to product twin."
    },
    "Partners orchestrated via open ecosystems": {
      keywords: "Catena-X, Gaia-X, platform networks",
      intent: "Choose if partner collaboration is managed via open platforms with standardized protocols.",
      automotive: "Supplier integration via Catena-X for traceability and ESG.",
      aerospace: "Additive vendors participate via aerospace ecosystem cloud.",
      electronics: "Open platform for ODM lifecycle synchronization.",
      machinery: "Vendors contribute data via neutral manufacturing data platform."
    }
  },
  
serviceRevenue: {
    "No service revenue model": {
      keywords: "product only, zero aftermarket",
      intent: "Select if business generates revenue only from product sale, with no spare parts, support, or services.",
      automotive: "Component vendor sells parts, no support or service.",
      aerospace: "Tooling supplier sells dies with no after-sale tracking.",
      electronics: "Sells gadgets with no warranty extension or maintenance.",
      machinery: "Delivers machine, no service contracts or parts."
    },
    "Ad-hoc spare part sales": {
      keywords: "reactive spares, no planning",
      intent: "Choose if spare part revenue is unplanned and happens only when requested.",
      automotive: "OEM sells brake pads on demand from dealers.",
      aerospace: "Occasional part replacements, no forecasting.",
      electronics: "Sells batteries and chargers reactively.",
      machinery: "Provides belts and fasteners when customer requests."
    },
    "Basic warranty services": {
      keywords: "limited repair, time-bound service",
      intent: "Pick if revenue includes services limited to warranty period or minimum coverage.",
      automotive: "Free service up to 3 years, minimal revenue.",
      aerospace: "Warranty-based fix or replace within 2 years.",
      electronics: "12-month warranty returns and repair support.",
      machinery: "Basic commissioning and within-1-year support."
    },
    "Scheduled maintenance contracts": {
      keywords: "preventive service, AMC",
      intent: "Use this if service revenue includes maintenance contracts or AMC-type services.",
      automotive: "Fleet customers sign annual maintenance packages.",
      aerospace: "Component vendor offers service-level guarantees.",
      electronics: "White-label AMC by ODM for consumer devices.",
      machinery: "3-year maintenance contract bundled with delivery."
    },
    "Spares revenue ≥ 10% of turnover": {
      keywords: "aftermarket profitability, repeat sales",
      intent: "Choose if spare parts generate meaningful recurring revenue.",
      automotive: "Filters, brake pads, and tires drive steady revenue.",
      aerospace: "High-margin spare parts sold post-warranty.",
      electronics: "OEM-branded cables and accessories sold regularly.",
      machinery: "Parts catalog drives post-sale procurement."
    },
    "Diagnostic or repair services offered": {
      keywords: "service team, post-sale expertise",
      intent: "Pick if dedicated teams offer repair, overhaul, or diagnostics for products sold.",
      automotive: "Dealer network offers diagnostics and software updates.",
      aerospace: "In-field MRO centers handle repairs.",
      electronics: "Walk-in repair centers or doorstep service available.",
      machinery: "Remote troubleshooting and on-site technician services."
    },
    "Usage-based or subscription billing": {
      keywords: "X-as-a-Service, metered billing",
      intent: "Select if revenue includes subscriptions, usage-based models, or leasing.",
      automotive: "Connected car features billed monthly.",
      aerospace: "Power-by-the-hour for engines.",
      electronics: "Annual license for cloud-connected features.",
      machinery: "Leased robot arm with monthly fee based on uptime."
    },
    "Digital services linked to physical product": {
      keywords: "data monetization, software layer",
      intent: "Choose if product generates recurring digital service revenue (e.g., apps, analytics).",
      automotive: "Navigation and infotainment software renewals.",
      aerospace: "Flight analytics subscription services.",
      electronics: "Cloud sync and diagnostics for smart devices.",
      machinery: "Digital twin updates and performance dashboards."
    },
    "Spare parts configurator or portal available": {
      keywords: "self-service, automated parts revenue",
      intent: "Use if customers can browse and order parts via digital self-service.",
      automotive: "Exploded view parts portal for repair shops.",
      aerospace: "Maintenance team uses PLM-based parts portal.",
      electronics: "Spare part ordering site for consumers.",
      machinery: "Online catalog with live stock status and pricing."
    },
    "Field service integrated into PLM/MES": {
      keywords: "service-data loop, structured feedback",
      intent: "Pick if service data flows back into product improvement lifecycle.",
      automotive: "Warranty claims tracked against product configurations.",
      aerospace: "Service bulletins linked to engineering change proposals.",
      electronics: "Service logs analyzed to improve firmware.",
      machinery: "Technician reports feed into next design cycle."
    },
    "Majority of revenue from service, not product": {
      keywords: "servitization, lifecycle value capture",
      intent: "Select if your model is dominated by services, not just selling physical goods.",
      automotive: "Fleet operator generates revenue from fleet uptime services.",
      aerospace: "Engine manufacturer earns more from support than engines.",
      electronics: "Revenue driven by platform services, not hardware.",
      machinery: "Heavy machinery firm shifts to outcome-based service contracts."
    }
  },
businessModelShift: {
    "Traditional product sales only": {
      keywords: "product-centric, one-time sale",
      intent: "Select if your business earns entirely from product sales without services, subscription, or platform extension.",
      automotive: "Sells vehicles, no subscription or software model.",
      aerospace: "Sells flight hardware with no aftermarket programs.",
      electronics: "One-time purchase of smartwatches, no services.",
      machinery: "Ships machines with no digital or recurring contract."
    },
    "Basic warranty and service bundles": {
      keywords: "extended support, value add-ons",
      intent: "Choose if services are offered only as optional extended warranties or delivery support.",
      automotive: "Adds 3-year extended service as post-sale.",
      aerospace: "Offers basic MRO with initial product.",
      electronics: "Add-on accident protection or setup service.",
      machinery: "Field install or remote support add-ons."
    },
    "Annual maintenance or AMC contracts": {
      keywords: "recurring post-sale support",
      intent: "Pick if services like installation, repair, or calibration generate recurring contracts.",
      automotive: "Fleet maintenance programs for commercial vehicles.",
      aerospace: "Per-aircraft component AMC offered to airlines.",
      electronics: "AV products with annual care plans.",
      machinery: "Calibration and health check plans bundled yearly."
    },
    "Modular software or firmware licensing": {
      keywords: "configurable software monetization",
      intent: "Select if product includes feature-unlocking or update revenue via software or firmware modules.",
      automotive: "Subscription to heated seats, ADAS modules.",
      aerospace: "Performance analytics module for leased aircraft.",
      electronics: "Smart TVs with premium software unlocks.",
      machinery: "CNC features activated via code on subscription."
    },
    "Product + Service bundling": {
      keywords: "solution delivery, bundled contracts",
      intent: "Choose if hardware and services are packaged together in a single business offering.",
      automotive: "Car+Charging+Navigation app package sold together.",
      aerospace: "Aircraft + fleet management + MRO suite bundle.",
      electronics: "Hardware with diagnostics app + cloud.",
      machinery: "Robotic cell with install + training bundle."
    },
    "Platform revenue from APIs or data": {
      keywords: "ecosystem model, platform extension",
      intent: "Pick this if you generate revenue from external users via platform data, APIs, or ecosystem use.",
      automotive: "Third-party apps built on in-vehicle API.",
      aerospace: "Flight ops tools use engine OEM data via API.",
      electronics: "Smart home hub monetized by integrations.",
      machinery: "Partners pay for digital platform access."
    },
    "Usage-based or pay-per-output pricing": {
      keywords: "XaaS, consumption-based",
      intent: "Select if customers pay based on how much or how long they use the product.",
      automotive: "Subscription driving model (e.g., Flex programs).",
      aerospace: "‘Thrust by the Hour’ service for engines.",
      electronics: "Cloud camera storage per usage plan.",
      machinery: "Uptime-based pricing for industrial tools."
    },
    "Value-based pricing with business outcome ties": {
      keywords: "outcome-linked, risk-sharing model",
      intent: "Choose if pricing is tied to customer’s business metrics or ROI.",
      automotive: "Fleet savings sharing model with OEM.",
      aerospace: "Availability-based payment for critical parts.",
      electronics: "Cost-per-uptime for enterprise devices.",
      machinery: "Charges tied to throughput gains."
    },
    "Product-as-a-Platform (PaaP) strategy": {
      keywords: "platform thinking, continuous monetization",
      intent: "Use this if your product is a platform with configurable services/modules monetized over time.",
      automotive: "EV car OS platform with app store.",
      aerospace: "Cockpit interface platform across aircraft lines.",
      electronics: "Modular smart device OS with integrations.",
      machinery: "Common platform for factory automation tools."
    },
    "Digital twin / simulation model monetization": {
      keywords: "model-based services, virtual validation",
      intent: "Pick this if your models, simulations, or digital twins are licensed or sold.",
      automotive: "Digital twin services for supplier validation.",
      aerospace: "Flight test simulator licensed to operators.",
      electronics: "Thermal model simulation service for clients.",
      machinery: "Sell process simulation packages for setup."
    },
    "Shift to complete service-based delivery": {
      keywords: "servitization, product-to-service",
      intent: "Select if product is no longer sold, but offered entirely as a service contract.",
      automotive: "Mobility-as-a-Service models replace ownership.",
      aerospace: "Jet engine leasing model dominates revenue.",
      electronics: "Device-as-a-Service bundles sold to B2B clients.",
      machinery: "Factory uptime services replace equipment sale."
    }
  },
lifecycleMonetization: {
    "No lifecycle monetization": {
      keywords: "one-time sale, no data reuse",
      intent: "Select if your business earns only at point of sale with no revenue from usage, support, or lifecycle insights.",
      automotive: "Sells vehicles, no aftermarket digital monetization.",
      aerospace: "No post-delivery engineering engagement.",
      electronics: "Sells devices without usage tracking or services.",
      machinery: "Ships machines, no feedback or data loops post-sale."
    },
    "Manual tracking of installed base": {
      keywords: "reactive tracking, basic install info",
      intent: "Pick if lifecycle data is tracked manually via spreadsheets, email or field calls.",
      automotive: "Excel used to track dealer sales.",
      aerospace: "Technicians report status manually.",
      electronics: "Warranty card registration only.",
      machinery: "Field engineer calls for install status."
    },
    "Warranty analytics inform service offerings": {
      keywords: "basic failure insight, historical data mining",
      intent: "Choose this if service decisions are influenced by warranty or failure analytics.",
      automotive: "Brake pad replacements suggested based on region failures.",
      aerospace: "Component redesign based on MRO issues.",
      electronics: "Service plan pricing adjusted based on returns.",
      machinery: "Machine redesign triggered by frequent failures."
    },
    "Service data flows into design improvement": {
      keywords: "closed-loop engineering, continuous improvement",
      intent: "Pick if service/usage data leads to product design improvements.",
      automotive: "Real-world battery data improves next-gen packs.",
      aerospace: "Cabin layout changed after usage studies.",
      electronics: "Button layout refined after field logs.",
      machinery: "Weld robot redesigned after customer feedback."
    },
    "Usage-based subscription linked to lifecycle": {
      keywords: "metered billing, data-triggered pricing",
      intent: "Select if subscriptions adapt based on usage patterns or lifecycle stage.",
      automotive: "Navigation plan renews based on usage.",
      aerospace: "Component lifecycle triggers service pricing.",
      electronics: "Usage-based cloud backup plans.",
      machinery: "Pricing adjusts based on uptime hours."
    },
    "Predictive analytics for lifecycle events": {
      keywords: "forecasting, event anticipation",
      intent: "Use if system predicts wear-outs, failures or upgrades before they occur.",
      automotive: "Tire wear prediction suggests service.",
      aerospace: "Flight hours predict turbine overhaul.",
      electronics: "Charging cycles predict battery drop-off.",
      machinery: "Bearings flagged for proactive replacement."
    },
    "Monetization via configuration data": {
      keywords: "customization billing, feature analytics",
      intent: "Choose if product config info drives personalization and value-based pricing.",
      automotive: "Luxury features activated based on profile.",
      aerospace: "Cabin layouts tailored and priced differently.",
      electronics: "Smartphone color/storage mix used in next batch planning.",
      machinery: "Custom tool configuration charged at premium."
    },
    "After-sales ecosystem monetization": {
      keywords: "partner revenue, consumables, add-ons",
      intent: "Pick if partners or consumables generate recurring revenue via lifecycle usage.",
      automotive: "Connected app marketplace for owners.",
      aerospace: "Add-on services offered to aircraft operators.",
      electronics: "Accessories, cloud, and services ecosystem.",
      machinery: "Lubricants and certified spare kits sold online."
    },
    "Customer engagement across product life": {
      keywords: "lifecycle CRM, behavioral targeting",
      intent: "Choose if engagement spans the product lifecycle from install to retirement.",
      automotive: "App pushes upgrade options and trade-ins.",
      aerospace: "Digital logbook across airframe lifecycle.",
      electronics: "Upgrade alerts and support at each milestone.",
      machinery: "End-of-life service suggestions and recycle offers."
    },
    "Digital twin used for lifecycle monetization": {
      keywords: "twin-driven insights, monetized virtual asset",
      intent: "Pick if lifecycle twin feeds into business offers, simulations, and advisory services.",
      automotive: "Twin used to offer warranty upsell based on behavior.",
      aerospace: "Lifecycle analytics sold to airline fleet ops.",
      electronics: "Twin enables predictive warranty extension.",
      machinery: "Twin powers service plan pricing models."
    },
    "Monetization across full lifecycle touchpoints": {
      keywords: "multi-touch revenue, full-loop value capture",
      intent: "Select if business captures value from install to disposal – hardware, data, services, updates.",
      automotive: "Recurring revenue from charging, connectivity, insurance.",
      aerospace: "Every flight cycle monetized via MRO + data.",
      electronics: "Renewals, resale, and trade-in incentives.",
      machinery: "Machine-as-a-service with recycling and redeployment models."
    }
  },
digitalThreadMonetization: {
    "No digital thread in place": {
      keywords: "disconnected systems, no feedback loop",
      intent: "Choose if product data does not flow across the lifecycle stages or systems (e.g., design → test → manufacturing).",
      automotive: "CAD, BOM, and shop floor tools not integrated.",
      aerospace: "Design team unaware of MRO events.",
      electronics: "No linkage between schematic, test, and field data.",
      machinery: "Engineering and production run separately, no data sync."
    },
    "Isolated lifecycle data silos": {
      keywords: "non-integrated, manual transfers",
      intent: "Select if data exists across stages but must be transferred manually or not reused effectively.",
      automotive: "MBOM reworked in Excel after design release.",
      aerospace: "Test data saved locally, not shared with design.",
      electronics: "Separate DBs for design and support tickets.",
      machinery: "Operator logs not linked to original specs."
    },
    "Manual digital thread efforts": {
      keywords: "partially connected, traceable on demand",
      intent: "Pick this if effort is made to stitch data across lifecycle, but process is manual or brittle.",
      automotive: "Engineers manually map CAD to quality reports.",
      aerospace: "ECAD and MRO reports linked via spreadsheet.",
      electronics: "Some traceability across PLM and ERP systems.",
      machinery: "Engineers recreate links at ECR level manually."
    },
    "Design-to-manufacture traceability achieved": {
      keywords: "forward link, EBOM-MBOM sync",
      intent: "Choose if the flow from design to production is traceable and structured.",
      automotive: "CAD-to-BOP integration enables manufacturing alignment.",
      aerospace: "3D models directly generate work instructions.",
      electronics: "Test points mapped from layout to MES.",
      machinery: "EBOM links to MBOM and BOP for each variant."
    },
    "Field data used to drive product decisions": {
      keywords: "feedback loop, usage-informed roadmap",
      intent: "Pick if usage data or customer logs influence engineering, quality or product strategy.",
      automotive: "Telematics data drives firmware updates.",
      aerospace: "Flight logs suggest design change for weight reduction.",
      electronics: "App crash reports inform feature roadmap.",
      machinery: "Field sensor anomalies modify next-gen design."
    },
    "Lifecycle analytics with product genealogy": {
      keywords: "traceability + analytics, root cause",
      intent: "Use if product, version, and usage history enable root-cause analysis or improvements.",
      automotive: "Chassis defect traced to specific lot with analytics.",
      aerospace: "Tail-specific flight history analyzed for performance.",
      electronics: "Battery wear patterns per batch analyzed.",
      machinery: "Weld inconsistencies analyzed per shift/operator."
    },
    "Digital twin integration with lifecycle stages": {
      keywords: "model-driven feedback loop, 360° product view",
      intent: "Select if a digital twin exists and is used across engineering, testing, and service.",
      automotive: "Digital twin used for virtual validation and tuning.",
      aerospace: "Simulated loads compare with real telemetry.",
      electronics: "Thermal model updated post-deployment.",
      machinery: "Twin compares performance to simulated baseline."
    },
    "Customers pay for insights from product usage": {
      keywords: "insights-as-a-service, analytics resale",
      intent: "Choose if analytics generated from product usage are monetized directly or indirectly.",
      automotive: "Fleet managers buy usage reports from OEM.",
      aerospace: "Airlines pay for predictive maintenance data.",
      electronics: "Dashboard insights sold as premium tier.",
      machinery: "Analytics dashboards included as service tier."
    },
    "Suppliers connected via digital thread": {
      keywords: "multi-enterprise traceability, ecosystem continuity",
      intent: "Pick if suppliers are included in lifecycle traceability with upstream/downstream visibility.",
      automotive: "Battery supplier updates twin after batch change.",
      aerospace: "Supplier part genealogy linked to aircraft records.",
      electronics: "ODM feeds test data into OEM’s lifecycle model.",
      machinery: "Vendor QC data visible in PLM."
    },
    "Digital thread used in pricing or warranty": {
      keywords: "context-aware monetization, risk-based pricing",
      intent: "Use if lifecycle intelligence affects how pricing, warranty, or service terms are decided.",
      automotive: "Driving behavior impacts warranty length.",
      aerospace: "Flight cycles and usage context affect service pricing.",
      electronics: "Warranty extensions based on usage profile.",
      machinery: "Runtime hours drive service package cost."
    },
    "Full digital thread monetization strategy in place": {
      keywords: "monetized twin, usage revenue streams",
      intent: "Select if digital threads are a core monetization driver (e.g., for upsell, service, risk mgmt).",
      automotive: "OEM sells driving analytics packages to end users.",
      aerospace: "Twin-enabled MRO services generate significant revenue.",
      electronics: "Device usage feeds loyalty and upgrade campaigns.",
      machinery: "PLM-powered service revenues exceed product margin."
    }
  },
  ipPortfolioLeverage: {
    "No connection between IP and product design": {
      keywords: "siloed patents, no visibility",
      intent: "Choose if patenting and engineering design are disconnected with no strategy or systems linking them.",
      automotive: "Patents filed post-facto, not aligned with CAD or BOM.",
      aerospace: "IP department unaware of design iterations.",
      electronics: "Innovation disclosures not tied to product roadmap.",
      machinery: "R&D files patents manually with no PLM data trace."
    },
    "Manual documentation for patent support": {
      keywords: "reactive patent docs, email-based",
      intent: "Pick this if engineers manually prepare documentation to support patent applications.",
      automotive: "Design screenshots and notes sent to legal via email.",
      aerospace: "PDFs exported from PLM to support IP filings.",
      electronics: "Word documents and annotated images prepared manually.",
      machinery: "Innovation logs kept offline or in spreadsheets."
    },
    "Basic IP tagging in PLM or ERP": {
      keywords: "limited metadata, light traceability",
      intent: "Use if basic tags or attributes are used to flag IP-related components or designs.",
      automotive: "Patent-covered parts tagged in BOM notes.",
      aerospace: "Engineering objects flagged as IP-sensitive.",
      electronics: "Circuit design marked as protected in PLM.",
      machinery: "Tool drawings labeled with IP codes manually."
    },
    "Dedicated IP metadata within engineering data": {
      keywords: "structured tagging, search-ready IP",
      intent: "Select if PLM objects contain searchable metadata on IP coverage or status.",
      automotive: "Design elements classified by patent class.",
      aerospace: "Wing structure variations tagged with IP type.",
      electronics: "PCB regions flagged with IP applicability.",
      machinery: "CAD data enriched with associated patent references."
    },
    "Innovation disclosure linked to PLM": {
      keywords: "engineer-driven IP creation",
      intent: "Pick if engineers can file invention disclosures from within the PLM interface.",
      automotive: "Design engineers submit disclosures via Active Workspace.",
      aerospace: "PLM captures concept maturity and invention triggers.",
      electronics: "Inventor data linked directly to BOM features.",
      machinery: "Prototypes logged in PLM also flagged for IP review."
    },
    "Collaboration between legal and R&D teams": {
      keywords: "cross-functional IP strategy",
      intent: "Choose if IP and engineering teams coordinate regularly using shared systems.",
      automotive: "IP team attends design reviews to flag novel features.",
      aerospace: "Legal reviews engineering logs via PLM dashboards.",
      electronics: "Weekly syncs between IP and architecture teams.",
      machinery: "IP managers subscribe to changes in concept phase."
    },
    "Automated IP risk or coverage analysis": {
      keywords: "IP dashboards, portfolio maps",
      intent: "Use if systems visualize IP coverage across BOM or CAD and highlight gaps.",
      automotive: "IP heatmaps on parts and modules in CAD.",
      aerospace: "Real-time dashboards show patent coverage gaps.",
      electronics: "Schematic view highlights unprotected blocks.",
      machinery: "PLM generates IP audit for product lines."
    },
    "Integrated patent lifecycle with product lifecycle": {
      keywords: "co-managed lifecycle, audit trace",
      intent: "Pick this if patents are treated like product assets, managed alongside part lifecycles.",
      automotive: "Patents versioned and linked to feature variants.",
      aerospace: "Maturity of patent tied to TRL (Tech Readiness Level).",
      electronics: "IP lifecycles matched to product releases.",
      machinery: "Patent renewals tied to active product configurations."
    },
    "IP monetization via licensing strategy": {
      keywords: "portfolio revenue, royalty streams",
      intent: "Select if business actively monetizes IP using licensing models tracked in PLM/IPM.",
      automotive: "Battery module design licensed to global OEMs.",
      aerospace: "Interior layout patents licensed to third-party suppliers.",
      electronics: "USB-C thermal design licensed to ecosystem players.",
      machinery: "Jig design franchised to local integrators."
    },
    "IP usage tracked in multi-product roadmap": {
      keywords: "cross-program leverage, efficiency",
      intent: "Choose if patent usage is tracked across multiple product families or programs.",
      automotive: "IP reused across SUV and EV variants.",
      aerospace: "Winglet patents applied across models.",
      electronics: "IP blocks reused across chipset series.",
      machinery: "Proprietary modules reused in robot variants."
    },
    "IP portfolio fully orchestrated via PLM": {
      keywords: "digital IP strategy, synchronized lifecycle",
      intent: "Pick this if PLM is the source of truth for IP strategy across ideation, design, protection, and monetization.",
      automotive: "IP strategy dashboard embedded in Active Workspace.",
      aerospace: "From innovation to patent to design revision tracked in PLM.",
      electronics: "PLM drives IP portfolio analytics and risk.",
      machinery: "IP strategy embedded in configuration, roadmap, and design traceability."
    }
  },
  programMarginUplift: {
    "No visibility into margin erosion": {
      keywords: "untracked cost leakage, unknown waste",
      intent: "Select if margin drivers like rework, changes, or poor design reuse are not measured or managed.",
      automotive: "Engineering changes lead to unknown supplier cost hikes.",
      aerospace: "No cost visibility across program variants.",
      electronics: "Over-engineered PCBs increase unit cost.",
      machinery: "Late scope creep leads to costly redesigns."
    },
    "Margins analyzed post-delivery only": {
      keywords: "delayed insight, missed savings",
      intent: "Pick if margin evaluations are only performed after product release or during cost audits.",
      automotive: "Program profitability reviewed after vehicle launch.",
      aerospace: "Margin reviews held only post flight certification.",
      electronics: "COST BOM reviews happen after MP start.",
      machinery: "Program cost vs plan assessed post delivery."
    },
    "Engineering aware of target costs": {
      keywords: "cost goal visibility, partial alignment",
      intent: "Choose if engineering has visibility into cost/margin targets but not tracked real-time.",
      automotive: "CAD designers see target cost at system level.",
      aerospace: "Structural engineers track weight-cost targets.",
      electronics: "EE teams know unit cost goal for key parts.",
      machinery: "BOM cost estimates visible in design phase."
    },
    "Cost driver libraries maintained": {
      keywords: "should-cost, parametric estimation",
      intent: "Use if your org maintains reusable cost models, rules, or templates to predict and manage cost during design.",
      automotive: "Should-cost templates for suspension, drivetrain.",
      aerospace: "Detailed cost blocks for composite structures.",
      electronics: "BoM templates priced by sourcing region.",
      machinery: "Jig & fixture costing rules by complexity class."
    },
    "Design reuse tracking to reduce margin loss": {
      keywords: "reuse index, margin-informed design",
      intent: "Select if design reuse is tracked to improve profitability and reduce redundancy.",
      automotive: "Shared HVAC across 5 platforms drives margin.",
      aerospace: "Cockpit reuse increases profit per aircraft.",
      electronics: "Reference designs reused across PCB layouts.",
      machinery: "Common drive train used across robot arms."
    },
    "Late change impact costed and visualized": {
      keywords: "change economics, ripple effect analysis",
      intent: "Pick if PLM enables visualization and cost estimation of late-stage changes to protect margin.",
      automotive: "Late part change flagged as $200K impact.",
      aerospace: "Change ripple affects 3 suppliers and 4 systems.",
      electronics: "Firmware update delay visualized as margin loss.",
      machinery: "New material requirement costs auto-analyzed."
    },
    "Cost-to-margin dashboards in PLM": {
      keywords: "real-time visibility, cost+design linkage",
      intent: "Choose this if engineering, sourcing, and program teams see margin implications live during design.",
      automotive: "Part cost + weight + margin shown in dashboard.",
      aerospace: "Live margin for cabin layout shown in PLM.",
      electronics: "Real-time profit per SKU simulated by BoM.",
      machinery: "Designers get alerts on margin-critical decisions."
    },
    "Simulation of margin uplift scenarios": {
      keywords: "what-if tools, trade-off analysis",
      intent: "Pick if PLM or costing tools help model impact of design or sourcing decisions on margin.",
      automotive: "Switching alloy → +$3M program uplift modeled.",
      aerospace: "Wing design change improves margin 6%.",
      electronics: "PCB stackup variant costed for margin delta.",
      machinery: "Alternate supplier option improves 2% gross margin."
    },
    "Supplier feedback loop on margin impact": {
      keywords: "cost collaboration, margin-informed sourcing",
      intent: "Choose if suppliers share input during design to align with margin and cost goals.",
      automotive: "Tier-1 gives feedback on high-cost designs.",
      aerospace: "Joint design-for-cost review held with supplier.",
      electronics: "ODM gives real-time cost deltas per feature.",
      machinery: "Vendor suggests cost-down part replacement."
    },
    "Program profitability tied to PLM KPIs": {
      keywords: "margin as PLM metric, governance-linked",
      intent: "Use if program margin is a tracked metric in PLM with workflow integration.",
      automotive: "PLM gates include margin compliance checks.",
      aerospace: "Program margin is part of project health metric.",
      electronics: "Profitability score shown in PLM dashboard.",
      machinery: "Project risk score includes margin alerting."
    },
    "Margin improvement is a core PLM outcome": {
      keywords: "profit-centric engineering, measurable ROI",
      intent: "Select if PLM is implemented with a strong business case around margin expansion and cost avoidance.",
      automotive: "PLM delivers +4% program margin via reuse + traceability.",
      aerospace: "Weight-driven margin savings tied to digital twin.",
      electronics: "PLM reduces $2M/year in overdesign losses.",
      machinery: "Margin improvement tracked as PLM success KPI."
    }
  },
  mAndAFrequency: {
  "No M&A activity in last 5 years": {
    keywords: "low structural change",
    intent: "Choose if your org structure has remained constant.",
    automotive: "Stable org, no asset acquisitions.",
    aerospace: "Last merger over a decade ago.",
    electronics: "Organic growth, no JV.",
    machinery: "No carve-outs or plant divestitures."
  },
  "Single divestment or carve-out": {
    keywords: "isolated portfolio change",
    intent: "Pick if a business unit was sold or divested.",
    automotive: "Engine line divested to supplier.",
    aerospace: "Maintenance unit carved out.",
    electronics: "Region-specific asset sold.",
    machinery: "Plant split into independent entity."
  },
  "Acquired small business or startup": {
    keywords: "partial integration, small footprint",
    intent: "Use if your firm acquired IP or niche capability.",
    automotive: "EV software startup acquired.",
    aerospace: "Battery tech firm absorbed.",
    electronics: "Sensor chip supplier acquired.",
    machinery: "Niche tooling vendor acquired."
  },
  "Merged with similar-sized firm": {
    keywords: "large integration, major structural change",
    intent: "Select if two equal players merged within 5 years.",
    automotive: "OEM + OEM strategic merge (e.g. FCA-PSA).",
    aerospace: "Tier 1 merger drives product overlap.",
    electronics: "Two brands consolidated globally.",
    machinery: "Tech + operations merged regionally."
  },
  "Frequent geographic acquisitions": {
    keywords: "global footprint growth",
    intent: "Pick if growth is driven by plant/network expansion.",
    automotive: "New assembly in ASEAN via M&A.",
    aerospace: "Hangar ops acquired globally.",
    electronics: "ODM & distribution merged across markets.",
    machinery: "Territory added via local acquisition."
  },
  "M&A leads to tool/PLM fragmentation": {
    keywords: "digital redundancy, migration pressure",
    intent: "Choose if toolchains multiplied due to M&A.",
    automotive: "5+ PLM systems in global plants.",
    aerospace: "Tool redundancy post integration.",
    electronics: "CAD/PLM/ERP disconnected.",
    machinery: "Different systems per acquired entity."
  },
  "M&A integration program live": {
    keywords: "ongoing data harmonization",
    intent: "Select if teams are actively integrating acquired entities.",
    automotive: "Part numbering rationalization in progress.",
    aerospace: "Multi-site CAD migration underway.",
    electronics: "PLM structure harmonization project active.",
    machinery: "BoM convergence initiative running."
  },
  "Product overlap due to M&A": {
    keywords: "portfolio duplication, cost pressure",
    intent: "Use if M&A has created redundant or competing products.",
    automotive: "Two SUVs with identical specs from brands.",
    aerospace: "Duplicated nacelle variants post acquisition.",
    electronics: "Smartphones across price points overlap.",
    machinery: "Two mixers with 80% similarity coexist."
  },
  "Brand strategy redefined post-M&A": {
    keywords: "repositioning, IP focus",
    intent: "Pick if brand strategy changed due to acquisition.",
    automotive: "EV brand added as innovation anchor.",
    aerospace: "Spun off brand for services business.",
    electronics: "Premium brand launched post tech M&A.",
    machinery: "Acquired brand positioned as mid-tier option."
  },
  "M&A-specific PLM instance created": {
    keywords: "transitional PLM zone, sandbox",
    intent: "Choose if separate PLM used temporarily for transition.",
    automotive: "Target company kept on legacy PLM during audit.",
    aerospace: "Sandbox for dual BOM reconciliation.",
    electronics: "PLM clone for acquired chip line.",
    machinery: "PLM used as data lake during onboarding."
  },
  "Full digital harmonization achieved post-M&A": {
    keywords: "unified PLM, digital continuity",
    intent: "Select if M&A integration now fully digital and harmonized.",
    automotive: "One PLM backbone across legacy and new assets.",
    aerospace: "Merged digital twin strategy in place.",
    electronics: "BoM structure unified across products.",
    machinery: "One lifecycle process across all regions."
  }
},
localizationDemand: {
  "Global product, no localization effort": {
    keywords: "one-size-fits-all, universal design",
    intent: "Choose if your product design is the same across all geographies with minimal or no localization.",
    automotive: "Same car spec sold globally without regional tailoring.",
    aerospace: "Aircraft interiors not localized for region-specific airlines.",
    electronics: "Devices lack local language or regulatory features.",
    machinery: "Industrial equipment without region-specific safety norms."
  },
  "Manual localization through external teams": {
    keywords: "outsourced translation, offline changes",
    intent: "Select if localization is handled post-design with no PLM traceability.",
    automotive: "Labels translated offline and updated manually.",
    aerospace: "Local variants created in PowerPoint or Excel.",
    electronics: "Language packs added late outside PLM.",
    machinery: "Installation instructions localized outside system."
  },
  "Region-specific specs tracked manually": {
    keywords: "semi-structured localization, ad hoc",
    intent: "Pick if localization exists but tracked manually or inconsistently.",
    automotive: "Separate CAD variants per market saved with suffixes.",
    aerospace: "Maintenance doc in local formats stored on file servers.",
    electronics: "Variants created per power/voltage standards.",
    machinery: "BoMs duplicated for each region without config logic."
  },
  "PLM supports country-specific configurations": {
    keywords: "variant logic, structured localization",
    intent: "Choose if regional features are managed via product configuration in PLM.",
    automotive: "Euro NCAP features added for EU market.",
    aerospace: "Regional cabin layouts tied to customer type.",
    electronics: "PLM switches content by market’s regulations.",
    machinery: "Safety compliance toggled per region in BOM logic."
  },
  "Compliance labels & docs tied to region": {
    keywords: "traceable documentation, regulatory linkage",
    intent: "Pick if your documentation, manuals, and labels are localized via PLM workflows.",
    automotive: "Manuals auto-generated per market in PLM.",
    aerospace: "Local certs linked to system test artifacts.",
    electronics: "Label packs triggered from PLM configuration.",
    machinery: "User manuals localized via template logic."
  },
  "Region-specific change management": {
    keywords: "regional workflow routing",
    intent: "Select if engineering changes trigger different approvals by geography.",
    automotive: "Change in LHD → only EU team notified.",
    aerospace: "Change to local avionics triggers Japan review.",
    electronics: "Region-specific tolerances drive change impact.",
    machinery: "Region-linked approvals based on plant target."
  },
  "Localization cost tracked across lifecycle": {
    keywords: "market-specific cost control",
    intent: "Use if localization overhead is tracked and optimized within product cost.",
    automotive: "Each market variant shows BOM cost delta.",
    aerospace: "Regional cabin layout drives cost estimate.",
    electronics: "Translation cost mapped to software build.",
    machinery: "Component cost per region calculated."
  },
  "Local regulatory logic embedded in PLM": {
    keywords: "regulation-driven config",
    intent: "Pick if PLM enforces localization based on built-in legal/business rules.",
    automotive: "Country rules pre-define allowed emissions tech.",
    aerospace: "Flight regulations impact loadout options.",
    electronics: "PLM validates power supply per country.",
    machinery: "Noise limits apply via config logic."
  },
  "Region-linked sourcing options": {
    keywords: "supply chain localization",
    intent: "Choose if local supplier options are tied to region configurations.",
    automotive: "EU variant selects Tier 1 EU-supplier by config.",
    aerospace: "Material sourcing tied to local specs.",
    electronics: "Boards sourced from geo-matched CM.",
    machinery: "Motors chosen based on country-specific tariff rules."
  },
  "End-to-end localization visibility": {
    keywords: "traceability, configuration control",
    intent: "Pick if the entire lifecycle (design → doc → service) supports visibility by region.",
    automotive: "Local sales team sees complete variant lineage.",
    aerospace: "Service procedures traceable to localized design.",
    electronics: "Product tree visible by country SKU.",
    machinery: "TechPub, BOM and training localized with trace."
  },
  "Localization managed as strategic differentiator": {
    keywords: "local-first engineering, market-fit",
    intent: "Choose if localization is planned and managed as a competitive advantage from the start.",
    automotive: "Vehicle platforms designed for fast market adaptation.",
    aerospace: "Cabin UX customized for airline brand identity.",
    electronics: "Design-to-local-market timeline tracked in PLM.",
    machinery: "Regional safety, energy, language specs embedded from concept."
  }
},
technicalDebt: {
  "All systems up to date, cloud native": {
    keywords: "modern architecture, agile-ready",
    intent: "Select this if the digital stack is cloud-native, maintained, and flexible.",
    automotive: "Latest Teamcenter on cloud, no legacy customizations.",
    aerospace: "SaaS PLM and modern integration landscape.",
    electronics: "Fully containerized microservices for lifecycle.",
    machinery: "Minimal technical debt, real-time architecture."
  },
  "Minor legacy systems with low impact": {
    keywords: "non-critical legacy",
    intent: "Pick if there are a few older tools but do not impact core processes.",
    automotive: "Outdated archive system, rest up-to-date.",
    aerospace: "Legacy HRIS not integrated to PLM.",
    electronics: "Toolchain updated except test equipment tools.",
    machinery: "Minor legacy tooling system, not on critical path."
  },
  "Moderate legacy footprint impacting speed": {
    keywords: "performance lag, upgrades delayed",
    intent: "Choose if legacy systems block automation, PLM agility.",
    automotive: "BoM system upgrade delayed by integrations.",
    aerospace: "CAD viewer crashes on new formats.",
    electronics: "PLM version blocks analytics rollout.",
    machinery: "Old servers block 24/7 data access."
  },
  "Heavy technical debt slows lifecycle speed": {
    keywords: "rework, duplicated logic",
    intent: "Pick if processes are error-prone or workarounds exist due to outdated platforms.",
    automotive: "Manual steps in workflow due to disconnected systems.",
    aerospace: "Revisions sent via email due to tool limits.",
    electronics: "Multiple tools for same function (e.g., 3 CAD viewers).",
    machinery: "No standard templates; high rework overhead."
  },
  "Upgrades blocked by past customizations": {
    keywords: "upgrade blockers, legacy constraints",
    intent: "Choose if historic customizations block feature adoption or upgrades.",
    automotive: "Workflow engine heavily customized in XML.",
    aerospace: "CAD extensions no longer supported in latest PLM.",
    electronics: "Migration blocked by custom object models.",
    machinery: "Legacy scripting causes crashes on new PLM builds."
  },
  "High IT maintenance cost due to tech debt": {
    keywords: "high TCO, patch management",
    intent: "Select if IT spends heavily to maintain old stack.",
    automotive: "Separate DevOps team just to patch legacy PLM.",
    aerospace: "Old DB engine needs manual reindexing.",
    electronics: "Patch incompatibilities block weekly CI/CD.",
    machinery: "Frequent production downtime due to stack instability."
  },
  "Dependency on retired or unsupported tech": {
    keywords: "unsupported stack, security exposure",
    intent: "Pick if tech stack includes tools no longer supported.",
    automotive: "Windows 7-based engineering workstations.",
    aerospace: "Oracle 10g powering backend.",
    electronics: "Custom PLM layer on deprecated stack.",
    machinery: "SFTP integration with tools no longer maintained."
  },
  "Integration complexity across lifecycle": {
    keywords: "point-to-point spaghetti",
    intent: "Choose if lifecycle tools are connected via custom scripts or brittle logic.",
    automotive: "No standard API for BoM ↔ ERP ↔ MES.",
    aerospace: "Manual sync of design-to-test data.",
    electronics: "Siloed platforms use middleware glue.",
    machinery: "Engineering tools use file-based transfers."
  },
  "Hardcoded logic within PLM workflows": {
    keywords: "low reusability, brittle automation",
    intent: "Pick if workflow behavior can’t be changed without deep code edits.",
    automotive: "Java-based hard logic for sign-offs.",
    aerospace: "No rules engine – all approvals coded.",
    electronics: "Custom scripts for every change variant.",
    machinery: "Workflow cannot adapt without redeploy."
  },
  "High training time due to UI complexity": {
    keywords: "usability issues, low adoption",
    intent: "Choose if tools are outdated or non-intuitive, causing resistance.",
    automotive: "Training for PLM takes 5+ sessions.",
    aerospace: "Users need cheat sheets for basic actions.",
    electronics: "UI not responsive or web-native.",
    machinery: "Too many fields and slow UX hinders usage."
  },
  "Active debt reduction roadmap funded": {
    keywords: "modernization plan, tech debt visibility",
    intent: "Pick if debt is known and being addressed strategically.",
    automotive: "PLM upgrade planned with vendor support.",
    aerospace: "Replatforming of test systems approved.",
    electronics: "Architecture modernization funding approved.",
    machinery: "PLM tech debt tracked and prioritized by IT strategy."
  }
},

  // Continue with 21 engineeringIPRisk
 

 
};
